import {
  deleteAdminRingGameIssueReport,
  deleteAdminRingGameIssueReportsByRingId,
  disableAdminRingGameIssueReportGroup,
  enterNextRingGameRound,
  getAdminActiveRingGamesCount,
  getAdminRingGameIssueReportDetails,
  getAdminRingGameIssueReportSummaries,
  getAdminRingGameMapAssets,
  getAdminRingGameOverallStats,
  getAdminRingGamePlayCounts,
  getAdminRingGameRankings,
  getAdminRingGameScoreDistribution,
  getMyRingGameHistory as getMyRingGameHistoryApi,
  getRingGameRank as getRingGameRankApi,
  reportRingIssueAndRerollCurrentRound as reportRingIssueAndRerollCurrentRoundApi,
  startRingGame as startRingGameApi,
  takeRingGameGuess as takeRingGameGuessApi,
} from "@/lib/api/generated/endpoints/ring-game/ring-game";
import type {
  ActiveRingGamesCountResponse,
  AdminRingGameRankingsResponse,
  DeleteRingGameIssueReportsResponse,
  DisableRingGameIssueReportGroupResponse,
  GetAdminRingGameIssueReportDetailsParams,
  GetAdminRingGameIssueReportSummariesParams,
  GetAdminRingGameRankingsParams,
  GetRingGameRankParams,
  ReportRingIssueResponse,
  RingGameHistoryRecord,
  RingGameIssueReportDetailsResponse,
  RingGameIssueReportSummariesResponse,
  AdminRingGameMapAssetsResponse,
  RingGameIdInput,
  RingGameOverallStatsResponse,
  RingGamePlayCount,
  RingGameRankRecord as ApiRingGameRankRecord,
  RingGameScoreDistributionBucket,
  RingRoundUpdate,
  StartRingGameResponse,
  TakeRingGameGuessInput,
  TakeRingGameGuessResponse,
} from "@/lib/api/generated/model";

export type {
  ActiveRingGamesCountResponse,
  AdminRingGameRankingsResponse,
  DeleteRingGameIssueReportsResponse,
  DisableRingGameIssueReportGroupResponse,
  GetAdminRingGameIssueReportDetailsParams,
  GetAdminRingGameIssueReportSummariesParams,
  GetAdminRingGameRankingsParams,
  GetRingGameRankParams,
  ReportRingIssueResponse,
  RingGameHistoryRecord,
  RingGameIssueReportDetailsResponse,
  RingGameIssueReportSummariesResponse,
  AdminRingGameMapAssetsResponse,
  RingGameIdInput,
  RingGameOverallStatsResponse,
  RingGamePlayCount,
  RingRoundUpdate,
  StartRingGameResponse,
  TakeRingGameGuessInput,
  TakeRingGameGuessResponse,
  RingGameScoreDistributionBucket,
};

export type RingGameRankRecord = Omit<ApiRingGameRankRecord, "user"> & {
  user: ApiRingGameRankRecord["user"] | null;
};

export function startRingGame(): Promise<StartRingGameResponse> {
  return startRingGameApi();
}

export function reportRingIssueAndRerollCurrentRound(
  input: RingGameIdInput,
): Promise<ReportRingIssueResponse> {
  return reportRingIssueAndRerollCurrentRoundApi(input);
}

export function enterNextRound(input: RingGameIdInput): Promise<ReportRingIssueResponse> {
  return enterNextRingGameRound(input);
}

export function takeAGuess(input: TakeRingGameGuessInput): Promise<TakeRingGameGuessResponse> {
  return takeRingGameGuessApi(input);
}

export function getRingGameRank(params?: GetRingGameRankParams): Promise<RingGameRankRecord[]> {
  return getRingGameRankApi(params).then((records) =>
    records.map((record) => ({
      ...record,
      user: record.user ?? null,
    })),
  );
}

export function getMyRingGameHistory(): Promise<RingGameHistoryRecord[]> {
  return getMyRingGameHistoryApi();
}

export function getRingGamePlayCounts(): Promise<RingGamePlayCount[]> {
  return getAdminRingGamePlayCounts();
}

export function getActiveGamesCount(): Promise<ActiveRingGamesCountResponse> {
  return getAdminActiveRingGamesCount();
}

export function getRingGameRankings(
  input: GetAdminRingGameRankingsParams,
): Promise<AdminRingGameRankingsResponse> {
  return getAdminRingGameRankings(input);
}

export function getScoreDistribution(): Promise<RingGameScoreDistributionBucket[]> {
  return getAdminRingGameScoreDistribution();
}

export function getOverallStats(): Promise<RingGameOverallStatsResponse> {
  return getAdminRingGameOverallStats();
}

export function getRingGameMapAssets(): Promise<AdminRingGameMapAssetsResponse> {
  return getAdminRingGameMapAssets();
}

export function getRingGameIssueReportSummaries(
  input: GetAdminRingGameIssueReportSummariesParams,
): Promise<RingGameIssueReportSummariesResponse> {
  return getAdminRingGameIssueReportSummaries(input);
}

export function getRingGameIssueReportDetails(
  input: GetAdminRingGameIssueReportDetailsParams,
): Promise<RingGameIssueReportDetailsResponse> {
  return getAdminRingGameIssueReportDetails(input);
}

export function deleteRingGameIssueReport(input: {
  reportId: string;
}): Promise<DeleteRingGameIssueReportsResponse> {
  return deleteAdminRingGameIssueReport(input.reportId);
}

export function deleteRingGameIssueReportsByRingId(input: {
  ringId: string;
}): Promise<DeleteRingGameIssueReportsResponse> {
  return deleteAdminRingGameIssueReportsByRingId(input.ringId);
}

export function disableRingGameIssueReportGroup(input: {
  ringId: string;
}): Promise<DisableRingGameIssueReportGroupResponse> {
  return disableAdminRingGameIssueReportGroup(input.ringId);
}
